import { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";

import Login from "./components/Login";
import Register from "./components/Register";
import Dashboard from "./components/Dashboard";
import Users from "./components/Users";
import WalletManagement from "./components/WalletManagement";
import Sidebar from "./components/Sidebar";
import UserProfile from "./components/UserProfile/UserProfile";
import TopupPage from "./components/TopupPage";
import Transactions from "./components/Transactions";
import Vendors from "./components/Vendors";
import ResetPassword from "./components/ResetPassword";
import ResetPasswordPage from "./components/ResetPasswordPage";
import SmartSelfie from "./components/SmartSelfie";
import FaceVerify from "./components/FaceVerify";

import { fetchUserById } from "./services/api";

/* ---------------- Face Register Guard ---------------- */
const FaceRegisterGuard = ({ userData, children }) => {
  const navigate = useNavigate();
  const location = useLocation();

    useEffect(() => {
      if (!userData) return;

      if (userData.aws_image_id) {
        // if previous page exists → go back
        if (window.history.length > 1) {
          navigate(-1);
        } else {
          // fallback if direct access / refresh
          navigate("/dashboard", { replace: true });
        }
      }
  }, [userData, navigate, location.pathname]);

  if (userData?.aws_image_id) return null;
  return children;
};

/* ---------------- App ---------------- */

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [roleId, setRoleId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  /* ---------- Bootstrap Auth + User ---------- */
  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem("adminToken");
      const uid = localStorage.getItem("userId");
      const rid = localStorage.getItem("roleId");

      if (!token || !uid || !rid) {
        setLoading(false);
        return;
      }

      try {
        const res = await fetchUserById(uid, token);
        setUserData(res.data ?? res);
        setIsAuthenticated(true);
        setUserId(Number(uid));
        setRoleId(Number(rid));
      } catch (err) {
        console.error("Auth bootstrap failed:", err);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  /* ---------- Sidebar responsive ---------- */
  useEffect(() => {
    const handleResize = () =>
      setIsSidebarOpen(window.innerWidth > 991);

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* ---------- Loader ---------- */
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  return (
    <Router>
      <Routes>

        {/* ---------------- Auth Routes ---------------- */}
        <Route
          path="/login"
          element={
            isAuthenticated
              ? <Navigate to={roleId === 1 ? "/dashboard" : "/user-profile"} />
              : <Login setIsAuthenticated={setIsAuthenticated} setRoleId={setRoleId} setUserId={setUserId} />
          }
        />

        <Route
          path="/register"
          element={
            isAuthenticated
              ? <Navigate to={roleId === 1 ? "/dashboard" : "/user-profile"} />
              : <Register setIsAuthenticated={setIsAuthenticated} setRoleId={setRoleId} setUserId={setUserId} />
          }
        />

        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/reset-password-link" element={<ResetPasswordPage />} />

        {/* ---------------- Admin Layout ---------------- */}
        {isAuthenticated && roleId === 1 && (
          <Route
            path="/*"
            element={
              <div className="d-flex">
                <Sidebar
                  isOpen={isSidebarOpen}
                  setIsOpen={setIsSidebarOpen}
                  setIsAuthenticated={setIsAuthenticated}
                />

                <div className={`flex-grow-1 main-content ${isSidebarOpen ? "sidebar-open" : "sidebar-closed"}`}>
                  <main className="p-3 theme-card-gery content-wrapper">
                    <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/users" element={<Users />} />
                      <Route path="/vendors" element={<Vendors />} />
                      <Route path="/wallet" element={<WalletManagement />} />
                      <Route path="/transactions" element={<Transactions />} />
                      <Route
                        path="/face/register"
                        element={
                          <FaceRegisterGuard userData={userData}>
                            <SmartSelfie />
                          </FaceRegisterGuard>
                        }
                      />
                    </Routes>
                  </main>
                </div>
              </div>
            }
          />
        )}

        {/* ---------------- User Routes ---------------- */}
        {isAuthenticated && roleId !== 1 && (
          <>
            <Route path="/user-profile" element={<UserProfile userId={userId} setIsAuthenticated={setIsAuthenticated} />} />
            <Route path="/topup" element={<TopupPage />} />
            <Route path="/face/scan" element={<FaceVerify />} />
            <Route
              path="/face/register"
              element={
                <FaceRegisterGuard userData={userData}>
                  <SmartSelfie />
                </FaceRegisterGuard>
              }
            />
            <Route path="/" element={<Navigate to="/user-profile" />} />
          </>
        )}

        {/* ---------------- Fallback ---------------- */}
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </Router>
  );
}

export default App;
